"use client";

import { Link } from "react-router-dom";
import { useCheckout } from "@/hooks/useCheckout";
import { useSubscription } from "@/hooks/useSubscription";
import { PRICING_PLANS, type PricingPlanId } from "@/lib/billing/plans";
import { CheckIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConversionVariant = "trial_expiring" | "tier_upgrade";

interface TrialConversionModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * "trial_expiring" — proactive prompt shown when trial is ending soon.
   * "tier_upgrade"   — shown when a trialing user hits a higher-tier gated feature.
   */
  variant?: ConversionVariant;
  /**
   * For "tier_upgrade" variant: the minimum tier required by the gated feature.
   * Filters the plan cards shown to only eligible upgrade options.
   */
  requiredTier?: "plus" | "pro";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysUntil(ms: number): number {
  return Math.max(0, Math.ceil((ms - Date.now()) / (1000 * 60 * 60 * 24)));
}

function pluralDays(n: number) {
  return `${n} day${n === 1 ? "" : "s"}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * TrialConversionModal
 *
 * In-app conversion prompt that deep-links directly to the Polar.sh subscription
 * checkout. Triggered in two scenarios:
 *
 * 1. "trial_expiring" — proactively shown when a trialing user has ≤ 3 days left
 *    on their free trial. Opens on dashboard load (managed by useTrialConversionPrompt).
 *
 * 2. "tier_upgrade" — shown when a trialing user navigates to a feature gated at a
 *    higher subscription tier (e.g., Plus or Pro) than they currently hold. The
 *    SubscriptionGate shows this modal instead of immediately redirecting to /pricing.
 *
 * Clicking "Subscribe Now" on any plan card initiates a Polar.sh hosted checkout
 * session directly, without routing through /pricing. On success the user returns
 * to /dashboard with a subscription=success banner.
 */
export function TrialConversionModal({
  open,
  onClose,
  variant = "trial_expiring",
  requiredTier,
}: TrialConversionModalProps) {
  const { trialEndsAt, tier } = useSubscription();
  const { startCheckout, loadingPlan, error } = useCheckout();

  const daysLeft = trialEndsAt ? daysUntil(trialEndsAt) : null;
  const isUrgent =
    variant === "trial_expiring" && daysLeft !== null && daysLeft <= 1;

  // For tier_upgrade, only surface plans at or above the required tier
  const eligiblePlans = PRICING_PLANS.filter((plan) => {
    if (variant !== "tier_upgrade" || !requiredTier) return true;
    if (requiredTier === "pro") return plan.id === "pro";
    return ["plus", "pro"].includes(plan.id);
  });

  const headlineText =
    variant === "tier_upgrade"
      ? "Upgrade to unlock this feature"
      : daysLeft === 0
        ? "Your free trial ends today"
        : daysLeft === 1
          ? "Your free trial ends tomorrow"
          : `Your free trial ends in ${pluralDays(daysLeft ?? 0)}`;

  const subText =
    variant === "tier_upgrade"
      ? `You're on the ${tier} plan. Upgrade to ${
          requiredTier === "pro" ? "Founder Pro" : "Founder Plus or above"
        } to access this feature.`
      : "Subscribe now to keep your communities running after the trial ends.";

  const handleSubscribe = (planId: PricingPlanId) => {
    startCheckout(planId, {
      successRedirect: "/dashboard?subscription=success",
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="trial-modal-title"
        className="relative z-50 w-full max-w-xl rounded-2xl border border-border bg-background shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-border px-6 py-5">
          {isUrgent && (
            <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              Ending Soon
            </div>
          )}
          <h2
            id="trial-modal-title"
            className="text-lg font-bold text-foreground"
          >
            {headlineText}
          </h2>
          <p className="mt-1 text-sm text-muted">{subText}</p>
        </div>

        {/* Plan cards */}
        <div className="p-6">
          <div
            className={cn(
              "grid gap-3",
              eligiblePlans.length === 1
                ? "grid-cols-1"
                : "grid-cols-1 sm:grid-cols-2"
            )}
          >
            {eligiblePlans.map((plan) => {
              const isRecommended =
                plan.badge === "Most Popular" ||
                (requiredTier === "pro" && plan.id === "pro");
              return (
                <div
                  key={plan.id}
                  className={cn(
                    "flex flex-col rounded-xl border p-4",
                    isRecommended
                      ? "border-primary/50 bg-primary/[0.04] ring-1 ring-primary/20"
                      : "border-border"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-foreground">
                        {plan.name}
                      </p>
                      <p className="mt-0.5 text-xs text-muted">
                        {plan.description}
                      </p>
                    </div>
                    {plan.badge && (
                      <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                        {plan.badge}
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-foreground">
                      ${plan.priceMonthly.toFixed(2)}
                    </span>
                    <span className="text-xs text-muted">/mo</span>
                  </div>

                  <ul className="my-3 flex-1 space-y-1.5">
                    {plan.features.slice(0, 3).map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-xs text-muted"
                      >
                        <CheckIcon className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full"
                    size="sm"
                    variant={isRecommended ? "default" : "outline"}
                    disabled={loadingPlan !== null}
                    onClick={() => handleSubscribe(plan.id as PricingPlanId)}
                  >
                    {loadingPlan === plan.id ? "Loading..." : "Subscribe Now"}
                  </Button>
                </div>
              );
            })}
          </div>

          {error && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          {/* Footer */}
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-muted">
              Cancel anytime &bull;{" "}
              <Link
                to="/pricing"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={onClose}
              >
                View all plans
              </Link>
            </p>
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-muted transition-colors hover:text-foreground"
            >
              {variant === "tier_upgrade" ? "Go back" : "Remind me later"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
